import sortBy from 'lodash/sortBy';

import { UserCount } from '@/types';
import { settingsState } from '@/shared/state/settings.svelte';
import { timeState } from '@/shared/state/time.svelte';
import { wowthingData } from '@/shared/stores/data';
import { logErrors } from '@/utils/log-errors';
import type { ManualDataHeirloomItem } from '@/types/data/manual/heirloom';
import type { ManualDataIllusionItem } from '@/types/data/manual/illusion';

import { DataUserAchievements } from './achievements.svelte';
import { DataUserDerived } from './derived.svelte';
import { DataUserGeneral } from './general.svelte';
import { DataUserQuests } from './quests.svelte';
import type { CharacterTask } from './types/tasks.svelte';

type GenericCategory<T> = {
    name: string;
    items: T[];
};
type DoGenericParameters<T, U> = {
    categories: T[];
    haveFunc: (item: U) => boolean;
    includeUnavailable: boolean;
    haveCountFunc?: (item: U) => number;
    totalCountFunc?: (item: U) => number;
};

type UserCounts = Record<string, UserCount>;

class UserState {
    public achievements = new DataUserAchievements();
    public derived = new DataUserDerived();
    public general = new DataUserGeneral();
    public quests = new DataUserQuests();

    public appearanceMasks = $derived.by(() => logErrors(() => this._appearanceMasks()));
    public heirloomStats = $derived.by(() => this._heirlooms());
    public illusionStats = $derived.by(() => this._illusions());
    public recipes = $derived.by(() => this.derived.doRecipes(this.general.characters));
    public reputations = $derived.by(() => this.derived.doReputations(this.general.characters));

    public mounts = $derived.by(() =>
        this.derived.doCollectible('mounts', wowthingData.manual.mountSets, (id) =>
            this.general.hasMountById.has(id)
        )
    );

    public pets = $derived.by(() =>
        this.derived.doCollectible('pets', wowthingData.manual.petSets, (id) =>
            this.general.hasPetById.has(id)
        )
    );

    public toys = $derived.by(() =>
        this.derived.doCollectible('toys', wowthingData.manual.toySets, (id) =>
            this.general.hasToyByItemId.has(id)
        )
    );

    public weeklyQuestIds = $derived.by(() => {
        const now = timeState.slowTime;

        const allQuestIds = new Set<number>();
        const characterIds = this.general.activeCharacters
            .filter((char) => char.weeklyReset > now)
            .map((char) => char.id);

        for (const characterId of characterIds) {
            const characterQuests = this.quests.characterById.get(characterId);
            if (characterQuests) {
                for (const questId of characterQuests.hasQuestById.values()) {
                    allQuestIds.add(questId);
                }
            }
        }

        return allQuestIds;
    });

    public something() {
        // TODO: fetch user/achievements/quests
        // TODO: process
        // this.general.process(userData);
    }

    public accountCurrency(currencyId: number) {
        const ret = $derived(
            sortBy(
                this.general.activeCharacters.filter(
                    (char) => (char.scannedCurrenciesUnix || 0) > 0
                ),
                (char) => -char.scannedCurrenciesUnix
            ).find((char) => !!char.currencies[currencyId])?.currencies?.[currencyId]
        );
        return ret;
    }

    public activeViewTasks = $derived.by(() => logErrors(this._activeViewTasks));
    private _activeViewTasks = () => {
        console.time('derived activeViewTasks');
        const ret: Record<number, Record<string, CharacterTask>> = {};
        for (const character of userState.general.activeCharacters) {
            ret[character.id] = this.derived.doActiveViewTasks(
                userState.weeklyQuestIds,
                character,
                userState.quests.characterById.get(character.id)
            );
        }
        console.timeEnd('derived activeViewTasks');
        return ret;
    };

    private _appearanceMasks() {
        const hasAppearanceBySource = $state.snapshot(this.general.hasAppearanceBySource);
        const ret = new Map<number, number>();

        for (const [appearanceIdString, items] of Object.entries(
            wowthingData.items.appearanceToItems
        )) {
            const appearanceId = parseInt(appearanceIdString);
            let mask = 0;

            for (const [itemId, modifier] of items) {
                // if (userData.hasSource.has(`${itemId}_${modifier}`)) {
                if (hasAppearanceBySource.has(itemId * 1000 + modifier)) {
                    const item = wowthingData.items.items[itemId];
                    mask |= item.classMask;
                }
            }

            ret.set(appearanceId, mask);
        }

        return ret;
    }

    private _heirlooms(): UserCounts {
        return this.doGeneric({
            categories: wowthingData.manual.heirlooms,
            includeUnavailable: settingsState.value.collections.hideUnavailable,
            haveFunc: (heirloom: ManualDataHeirloomItem) =>
                this.general.heirlooms.get(
                    wowthingData.static.heirloomByItemId.get(heirloom.itemId).id
                ) !== undefined,
            totalCountFunc: (heirloom: ManualDataHeirloomItem) =>
                wowthingData.static.heirloomByItemId.get(heirloom.itemId).upgradeBonusIds.length +
                1,
            haveCountFunc: (heirloom: ManualDataHeirloomItem) => {
                const staticHeirloom = wowthingData.static.heirloomByItemId.get(heirloom.itemId);
                const userCount = this.general.heirlooms.get(staticHeirloom.id);
                return userCount !== undefined ? userCount + 1 : 0;
            },
        });
    }

    private _illusions(): UserCounts {
        return this.doGeneric({
            categories: wowthingData.manual.illusions,
            includeUnavailable: settingsState.value.collections.hideUnavailable,
            haveFunc: (illusion: ManualDataIllusionItem) =>
                userState.general.hasIllusionByEnchantmentId.has(illusion.enchantmentId),
        });
    }

    private doGeneric<T extends GenericCategory<U>, U>(
        params: DoGenericParameters<T, U>
    ): UserCounts {
        const counts: UserCounts = {};
        const overallData = (counts['OVERALL'] = new UserCount());

        for (const category of params.categories) {
            const categoryUnavailable = category.name.startsWith('Unavailable');
            const availabilityData = (counts[categoryUnavailable ? 'UNAVAILABLE' : 'AVAILABLE'] ||=
                new UserCount());
            const categoryData = (counts[category.name] = new UserCount());

            for (const item of category.items) {
                const userHas = params.haveFunc(item);

                if (categoryUnavailable && params.includeUnavailable !== true && !userHas) {
                    continue;
                }

                const totalCount = params.totalCountFunc?.(item) || 1;
                overallData.total += totalCount;
                availabilityData.total += totalCount;
                categoryData.total += totalCount;

                if (userHas) {
                    const haveCount = params.haveCountFunc?.(item) || 1;
                    overallData.have += haveCount;
                    availabilityData.have += haveCount;
                    categoryData.have += haveCount;
                }
            }
        }

        return counts;
    }
}

export const userState = new UserState();
